import { BodyComposition, WellnessItem } from './DataFormatService';

export function getSampleBodyComposition(): BodyComposition {
    return {
        bodyMassGrams: 77000, // 77 kg
        volumeCubicMillimeters: 77594132, // 77 liters
        bodyFatMass: 15400, // 20% of body mass
        fatFreeMass: 61600, // 80% of body mass
        totalBodyWaterMass: 46200, // 60% of body mass
        totalBodyProteinMass: 12000, // Approx. 15% of body mass
        totalBodyBoneMineralMass: 3500, // Approx. 4.5% of body mass
        bodyCellMass: 30800, // Approx. 40% of body mass
        intraCellularWaterMass: 30800, // Approx. 40% of body mass
        extraCellularWaterMass: 15400, // Approx. 20% of body mass
        fatFreeExtraCellularSolidsMass: 7700, // Approx. 10% of body mass
        intraCellularProteinMass: 9240, // Approx. 12% of body mass
        extraCellularProteinMass: 2760, // Approx. 3.6% of body mass
        basalMetabolicRate: 1800, // kcal/day
        bodySurfaceArea: 1.9, // m²
        waistToHip: 0.9, // Ratio
        waistToHeight: 0.5, // Ratio
        waistToThigh: 1.5, // Ratio
        waistToCalf: 2.5, // Ratio
        bodyMassIndex: 24, // kg/m²
        bodyRoundnessIndex: 4, // Dimensionless
        bodyAdiposityIndex: 25, // Dimensionless
        abdominalVolumeIndex: 12, // Dimensionless
        conicityIndex: 1.25, // Dimensionless
    };
}

export function getSampleWellnessData(): { [p: string]: WellnessItem } {
    return {
        height: {
            name: 'Height',
            value: 177,
            formattedValue: '177.0',
            formattedWithPostfix: '177.0 cm',
            unitLabel: 'cm',
        },
        weight: {
            name: 'Weight',
            value: 77,
            formattedValue: '77.0',
            formattedWithPostfix: '77.0 kg',
            unitLabel: 'kg',
        },
        heartRate: {
            name: 'Heart Rate',
            value: 70,
            formattedValue: '70.0',
            formattedWithPostfix: '70.0 beats/min',
            dialGroup: 4,
            unitLabel: 'beats/min',
            min: 60,
            max: 100,
        },
        breathing: {
            name: 'Breathing',
            value: 16,
            formattedValue: '16.0',
            formattedWithPostfix: '16.0 breaths/min',
            dialGroup: 4,
            unitLabel: 'breaths/min',
            min: 12,
            max: 20,
        },
        systolicBP: {
            name: 'Systolic BP (standing)',
            value: 120,
            formattedValue: '120',
            formattedWithPostfix: '120 mmHg',
        },
        diastolicBP: {
            name: 'Diastolic BP (standing)',
            value: 80,
            formattedValue: '80',
            formattedWithPostfix: '80 mmHg',
        },
        heartRateVariability: {
            name: 'Heart Rate Variability',
            value: 50,
            formattedValue: '50.0',
            formattedWithPostfix: '50.0 ms',
            dialGroup: 2,
            unitLabel: 'ms',
            min: 20,
            max: 100,
        },
        irregularHeartBeats: {
            name: 'Irregular heartbeat Count',
            value: 0,
            formattedValue: '0',
            formattedWithPostfix: '0',
        },
        cardiacWorkload: {
            name: 'Cardiac Workload',
            value: 3.9,
            formattedValue: '3.9',
            formattedWithPostfix: '3.9 dB',
            dialGroup: 2,
            unitLabel: 'dB',
            min: 3.71,
            max: 4.28,
        },
        vascularCapacity: {
            name: 'Vascular Capacity',
            value: 2.2,
            formattedValue: '2.2',
            formattedWithPostfix: '2.2 sec',
            dialGroup: 4,
            unitLabel: 'sec',
            min: 0.3,
            max: 3,
        },
        bmi: {
            name: 'Body Mass Index',
            value: 24.6,
            formattedValue: '24.6',
            formattedWithPostfix: '24.6 kg/m²',
            dialGroup: 2,
            unitLabel: 'kg/m²',
            min: 18.5,
            max: 24.9,
        },
        waistToHeight: {
            name: 'Waist-to-Height Ratio',
            value: 50,
            formattedValue: '50.0',
            formattedWithPostfix: '50.0%',
            dialGroup: 2,
            unitLabel: '%',
            min: 40,
            max: 60,
        },
        facialAge: {
            name: 'Facial Skin Age',
            value: 28,
            formattedValue: '28',
            formattedWithPostfix: '28 years',
        },
        cardioDiseaseRisk: {
            name: 'Cardiac Disease Risk',
            value: 5,
            formattedValue: '5.0',
            formattedWithPostfix: '5.0%',
        },
        heartAttackRisk: {
            name: 'Heart Attack Risk',
            value: 3,
            formattedValue: '3.0',
            formattedWithPostfix: '3.0%',
        },
        strokeRisk: {
            name: 'Stroke Risk',
            value: 2,
            formattedValue: '2.0',
            formattedWithPostfix: '2.0%',
        },
        vitalScore: {
            name: 'Vitals',
            value: 4.5,
            formattedValue: '4.5',
            formattedWithPostfix: '4.5/5.0',
        },
        physicalScore: {
            name: 'Physical',
            value: 4,
            formattedValue: '4.0',
            formattedWithPostfix: '4.0/5.0',
        },
        physiologicalScore: {
            name: 'Physiological',
            value: 4,
            formattedValue: '4.0',
            formattedWithPostfix: '4.0/5.0',
        },
        mentalStressIndex: {
            name: 'Mental Stress Index',
            value: 2,
            formattedValue: '2.0',
            formattedWithPostfix: '2.0/5.0',
            dialGroup: 3,
        },
        mentalScore: {
            name: 'Mental',
            value: 4,
            formattedValue: '4.0',
            formattedWithPostfix: '4.0/5.0',
        },
        risksScore: {
            name: 'Risks',
            value: 2,
            formattedValue: '2.0',
            formattedWithPostfix: '2.0/5.0',
        },
        wellnessScore: {
            name: 'General Wellness',
            value: 80,
            formattedValue: '80',
            formattedWithPostfix: '80/100',
            dialGroup: 3,
        },
        snr: {
            name: 'SNR',
            value: 3.7,
            formattedValue: '3.7',
            formattedWithPostfix: '3.7 dB',
        },
    };
}
